import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  SunIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { signOut } from "../../backend/client";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../context/actions/authActions";
import { auth } from "../../firebaseConfig";
import waselLogo from "../images/wasel-logo.png";
import waselSidebarLogo from "../images/wasel-sidebar-logo.png";
import {
  navigationItems,
  navigationSections,
} from "../../config/adminNavigation";

function BrandMark({ compact = false }) {
  return (
    <span
      className={`brand-mark ${compact ? "brand-mark--compact" : ""}`}
      aria-hidden="true"
    >
      <img src={waselLogo} alt="" />
    </span>
  );
}

function SidebarLink({ item, active, onNavigate }) {
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      className={`admin-nav-link ${active ? "admin-nav-link--active" : ""}`}
      aria-current={active ? "page" : undefined}
    >
      <Icon className="admin-nav-link__icon" aria-hidden="true" />
      <span>{item.label}</span>
      <ChevronLeftIcon className="admin-nav-link__arrow" aria-hidden="true" />
    </Link>
  );
}

export default function NavBar({ theme, onToggleTheme }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const user = useSelector((state) => state.auth.user);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);

  const activeItem = useMemo(() => {
    if (location.pathname.startsWith("/edit-client/")) {
      return navigationItems.find((item) => item.path === "/customers");
    }
    if (location.pathname.startsWith("/chat/")) {
      return navigationItems.find((item) => item.path === "/item_reports");
    }
    return (
      navigationItems.find((item) => item.path === location.pathname) ||
      navigationItems[0]
    );
  }, [location.pathname]);

  const searchResults = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase("ar");
    if (!normalizedQuery) return [];
    return navigationItems
      .filter((item) =>
        `${item.label} ${item.description}`
          .toLocaleLowerCase("ar")
          .includes(normalizedQuery),
      )
      .slice(0, 6);
  }, [searchQuery]);

  useEffect(() => {
    setMobileOpen(false);
    setSearchQuery("");
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if (
        event.key === "/" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(
          document.activeElement?.tagName,
        )
      ) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
      if (event.key === "Escape") {
        setSearchQuery("");
        setProfileOpen(false);
        setMobileOpen(false);
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const handleNavigate = () => {
    setMobileOpen(false);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (!searchResults.length) return;
    navigate(searchResults[0].path);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
    }
    dispatch(logout());
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpiration");
    localStorage.removeItem("loginAt");
  };

  const displayName = user?.displayName || "مدير النظام";
  const displayEmail = user?.email || "Wasel Admin";
  const initials = displayName.trim().charAt(0) || "و";

  return (
    <>
      <button
        type="button"
        className={`admin-sidebar-overlay ${mobileOpen ? "is-visible" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-label="إغلاق القائمة"
        tabIndex={mobileOpen ? 0 : -1}
      />

      <aside
        id="admin-sidebar"
        className={`admin-sidebar ${mobileOpen ? "is-open" : ""}`}
      >
        <div className="admin-sidebar__brand">
          <Link
            to="/"
            className="admin-sidebar__brand-logo"
            onClick={handleNavigate}
            aria-label="لوحة إدارة واصل"
          >
            <img src={waselSidebarLogo} alt="واصل WASEL" />
          </Link>
          <button
            type="button"
            className="icon-button admin-sidebar__close"
            onClick={() => setMobileOpen(false)}
            aria-label="إغلاق القائمة"
          >
            <XMarkIcon aria-hidden="true" />
          </button>
        </div>

        <nav className="admin-sidebar__nav" aria-label="التنقل الرئيسي">
          {navigationSections.map((section) => (
            <div className="admin-nav-section" key={section.label}>
              <p className="admin-nav-section__label">{section.label}</p>
              <div className="admin-nav-section__items">
                {section.items.map((item) => (
                  <SidebarLink
                    key={item.path}
                    item={item}
                    active={activeItem?.path === item.path}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <div className="admin-sidebar__status">
            <span className="status-dot" />
            <span>
              <strong>جلسة الإدارة نشطة</strong>
              <small>مسجّل الدخول الآن</small>
            </span>
          </div>
          <button type="button" className="admin-logout" onClick={handleLogout}>
            <ArrowLeftOnRectangleIcon aria-hidden="true" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      <header className="admin-topbar">
        <div className="admin-topbar__start">
          <button
            type="button"
            className="icon-button admin-menu-button"
            onClick={() => setMobileOpen(true)}
            aria-label="فتح القائمة"
            aria-expanded={mobileOpen}
            aria-controls="admin-sidebar"
          >
            <Bars3Icon aria-hidden="true" />
          </button>
          <div className="admin-page-heading">
            <span>لوحة إدارة واصل</span>
            <h1>{activeItem?.label || "لوحة التحكم"}</h1>
          </div>
        </div>

        <div className="admin-topbar__actions">
          <form
            className="admin-search"
            onSubmit={handleSearchSubmit}
            role="search"
          >
            <button
              type="button"
              className="admin-search-trigger"
              aria-label="فتح البحث"
              onClick={() => searchInputRef.current?.focus()}
            >
              <MagnifyingGlassIcon aria-hidden="true" />
            </button>
            <input
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="ابحث في لوحة التحكم"
              aria-label="البحث في أقسام لوحة التحكم"
              autoComplete="off"
            />
            <kbd>/</kbd>
            {searchQuery && (
              <div className="admin-search-results">
                {searchResults.length ? (
                  searchResults.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        type="button"
                        key={item.path}
                        onClick={() => navigate(item.path)}
                      >
                        <span className="admin-search-result__icon">
                          <Icon aria-hidden="true" />
                        </span>
                        <span>
                          <strong>{item.label}</strong>
                          <small>{item.description}</small>
                        </span>
                        <ChevronLeftIcon aria-hidden="true" />
                      </button>
                    );
                  })
                ) : (
                  <p>لا توجد نتائج مطابقة.</p>
                )}
              </div>
            )}
          </form>

          <button
            type="button"
            className="icon-button"
            onClick={onToggleTheme}
            aria-label={
              theme === "dark" ? "تفعيل الوضع الفاتح" : "تفعيل الوضع الداكن"
            }
            title={theme === "dark" ? "الوضع الفاتح" : "الوضع الداكن"}
          >
            {theme === "dark" ? (
              <SunIcon aria-hidden="true" />
            ) : (
              <MoonIcon aria-hidden="true" />
            )}
          </button>

          <div className="admin-profile">
            <button
              type="button"
              className="admin-profile__button"
              onClick={() => setProfileOpen((current) => !current)}
              aria-expanded={profileOpen}
              aria-haspopup="menu"
            >
              <span className="admin-profile__avatar">{initials}</span>
              <span className="admin-profile__copy">
                <strong>{displayName}</strong>
                <small>{displayEmail}</small>
              </span>
            </button>
            {profileOpen && (
              <div className="admin-profile__menu" role="menu">
                <div>
                  <strong>{displayName}</strong>
                  <small>{displayEmail}</small>
                </div>
                <button type="button" onClick={handleLogout} role="menuitem">
                  <ArrowLeftOnRectangleIcon aria-hidden="true" />
                  تسجيل الخروج
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

export { BrandMark };
